import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SystemRoleName } from '@evimo/types/enums';
import { useAuth } from '~/app/auth/context/AuthContext';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import {
  isAwaitingKmsExtraction,
  isAwaitingKmsLink,
  resolveKmsTypeCandidates,
  shouldShowKmsTypeCandidates,
} from '~/app/services/kms/kms-document.utils';

export function useKmsDocumentDetail() {
  const { documentId = '' } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;

  const organizationIdParam = searchParams.get('organizationId');
  const organizationId: string | null = useMemo(() => {
    if (!isSuperAdmin) {
      return user?.organizationId ?? null;
    }
    if (organizationIdParam === 'platform' || organizationIdParam === '') {
      return null;
    }
    if (organizationIdParam) {
      return organizationIdParam;
    }
    return user?.organizationId ?? null;
  }, [isSuperAdmin, organizationIdParam, user?.organizationId]);

  const documentQuery = useQuery({
    queryKey: ['kms', 'document', documentId, organizationId],
    queryFn: () =>
      KmsDocumentService.getStatus(documentId, organizationId, isSuperAdmin),
    enabled: Boolean(documentId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return false;
      }
      if (data.status === 'processing') {
        return 2_000;
      }
      if (isAwaitingKmsExtraction(data)) {
        return 2_000;
      }
      if (isAwaitingKmsLink(data)) {
        return 2_000;
      }
      return false;
    },
  });

  const document = documentQuery.data ?? null;
  const typeCandidates = useMemo(
    () => resolveKmsTypeCandidates(document),
    [document],
  );
  const showTypeCandidates = useMemo(
    () => shouldShowKmsTypeCandidates(document),
    [document],
  );

  return {
    documentId,
    organizationId,
    isSuperAdmin,
    document,
    typeCandidates,
    showTypeCandidates,
    isLoading: documentQuery.isLoading,
    error:
      documentQuery.error instanceof Error
        ? documentQuery.error.message
        : documentQuery.isError
          ? 'Failed to load document'
          : null,
    onBack: () => navigate('/kms?tab=library'),
  };
}
